import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null; needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isDoctor: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDoctor, setIsDoctor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Track which user ID we already loaded roles for — avoid re-querying on
  // every TOKEN_REFRESHED event (fires every ~60s and was spamming timeouts)
  const loadedRolesForRef = useRef('');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Only re-fetch roles when the user actually changes (sign in / user switch),
          // NOT on TOKEN_REFRESHED / MFA_CHALLENGE / USER_UPDATED which fire constantly
          const uid = session.user.id;
          const shouldRefetch = event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || loadedRolesForRef.current !== uid;
          if (shouldRefetch) {
            loadedRolesForRef.current = uid;
            // AWAIT roles before clearing isLoading — ProtectedRoute and Login
            // must not render until roles are known, otherwise admins get bounced to /
            await checkUserRoles(uid);
          }
        } else {
          loadedRolesForRef.current = '';
          setIsDoctor(false);
          setIsAdmin(false);
        }

        // Only mark loading as done AFTER roles are resolved
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkUserRoles = async (userId: string) => {
    try {
      const rolesQuery = supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      const timeout = new Promise<{ data: null; error: Error }>(resolve =>
        setTimeout(() => resolve({ data: null, error: new Error('timeout') }), 5000)
      );

      const { data: roles, error } = await Promise.race([rolesQuery, timeout]) as any;

      if (error) {
        console.warn('[Auth] Could not load roles:', error.message);
        return;
      }

      const roleList = roles?.map((r: any) => r.role) || [];
      console.debug('[Auth] Roles for', userId, '→', roleList.length ? roleList : '(none)');
      setIsDoctor(roleList.includes('doctor'));
      setIsAdmin(roleList.includes('admin'));
    } catch (err: any) {
      console.warn('[Auth] Could not load roles:', err?.message);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: { full_name: fullName || '' },
        },
      });
      if (error) return { error: new Error(error.message), needsEmailConfirmation: false };

      // If session is null after signup, Supabase requires email confirmation
      const needsEmailConfirmation = !data.session;
      return { error: null, needsEmailConfirmation };
    } catch (err) {
      return { error: err as Error, needsEmailConfirmation: false };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error: error ? new Error(error.message) : null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsDoctor(false);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        signUp,
        signIn,
        signOut,
        isDoctor,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
